import React from "react";
import { CheckCircle2 } from "lucide-react";
import { ThemeToggle } from "../../../shared/ui/ThemeToggle";
import { Link } from "react-router";
import { useForm } from "react-hook-form";
import { useAuth } from "../AuthContext";
import { checkUsername, checkBusiness, validateBusiness, createUser } from "../../../api/account";
import { redirect } from "react-router";

interface SignupFormInputs {
  username: string;
  password: string;
  password_confirm: string;
  bus_num: string;
  bus_started_at: string;
  rep_name: string;
  is_bus_validated: boolean;
}

const USERNAME_PATTERN = /^[A-Za-z0-9_]+$/;
const BUS_STARTED_AT_PATTERN = /^\d{8}$/;



const SignUpPage: React.FC = () => {
  const auth = useAuth();

  const [formErrorMessage, setFormErrorMessage] = React.useState<string | null>(null);
  const [formProcessingMessage, setFormProcessingMessage] = React.useState<string | null>(null);
  const [businessValidationMessage, setBusinessValidationMessage] = React.useState<string | null>(null);
  const [isBusinessValidated, setIsBusinessValidated] = React.useState<boolean>(false);
  const [isSignupComplete, setIsSignupComplete] = React.useState<boolean>(false);
  const { register, handleSubmit, formState: { errors }, setError, clearErrors, watch } = useForm<SignupFormInputs>({
    defaultValues: import.meta.env.DEV ? {
      bus_num: '650-14-02116',
      bus_started_at: '20240118',
      rep_name: '강민지',
    } : undefined,
    mode: 'onChange',
  });
  const busNum = watch("bus_num");
  const repName = watch("rep_name");
  const busStartedAt = watch("bus_started_at");


  // 사업자등록번호 포맷팅 함수 (3자리-2자리-5자리)
  const formatBusinessNumber = (value: string) => {
    // 숫자만 추출
    const numbers = value.replace(/[^\d]/g, '');

    // 10자리까지만 허용하고 3-2-5 형식으로 포맷팅
    if (numbers.length <= 3) {
      return numbers;
    } else if (numbers.length <= 5) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    } else if (numbers.length <= 10) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 5)}-${numbers.slice(5)}`;
    } else {
      // 10자리 초과 시 10자리까지만 사용
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 5)}-${numbers.slice(5, 10)}`;
    }
  };

  // 사업자등록번호에서 하이픈 제거
  const cleanBusinessNumber = (value: string) => {
    return value.replace(/[^\d]/g, '');
  };

  const handleBusinessValidation = async () => {
    const cleanedBusNum = cleanBusinessNumber(busNum || '');

    if (!cleanedBusNum || !repName || !busStartedAt) {
      setBusinessValidationMessage("모든 사업자 정보를 입력해주세요.");
      return;
    }

    if (cleanedBusNum.length !== 10) {
      setBusinessValidationMessage("사업자등록번호는 10자리여야 합니다.");
      return;
    }

    try {
      setBusinessValidationMessage("사업자 정보를 확인 중입니다…");
      const response = await checkBusiness({
        bus_num: cleanedBusNum,
      });
      if (!response || !response.data) {
        throw new Error()
      }
      if (response.data.success) {
        setBusinessValidationMessage(response.data.message);
        setIsBusinessValidated(true);
      } else {
        setBusinessValidationMessage(response.data.message);
        setIsBusinessValidated(false);
        return
      }
    } catch (error: any) {
      let message = "시스템 오류입니다. 잠시 후 다시 시도하세요.";
      setBusinessValidationMessage(message);
      setIsBusinessValidated(false);
      return
    }

    try {
      setBusinessValidationMessage("사업자 정보를 검증 중입니다…");
      const response = await validateBusiness({
        bus_num: cleanedBusNum,
        rep_name: repName,
        bus_started_at: busStartedAt,
      });
      if (await response?.data?.success) {
        setBusinessValidationMessage("사업자 정보가 검증되었습니다.");
        setIsBusinessValidated(true);
      } else {
        setBusinessValidationMessage("사업자 정보 검증에 실패했습니다.");
        setIsBusinessValidated(false);
      }
    } catch (error: any) {
      let message = "시스템 오류입니다. 잠시 후 다시 시도하세요.";
      setBusinessValidationMessage(message);
      setIsBusinessValidated(false);
    }
  };

  const onSubmit = async (data: SignupFormInputs) => {
    clearErrors("password_confirm");

    if (!isBusinessValidated) {
      setFormErrorMessage("사업자 정보 검증을 완료해주세요.");
      return;
    }

    const response = await checkUsername({
      username: data.username
    });

    if (response && response.data?.data?.is_exist) {
      setError("username", { type: "manual", message: "이미 사용 중인 사용자명입니다." });
      return;
    }
    if (data.password !== data.password_confirm) {
      setError("password_confirm", { type: "manual", message: "비밀번호가 일치하지 않습니다." });
      return;
    }
    await createUser({
      auth: auth,
      payload: {
        username: data.username,
        password: data.password,
        password_confirm: data.password_confirm,
        bus_num: data.bus_num,
        rep_name: data.rep_name,
        bus_started_at: data.bus_started_at,
      },
      setError: setError,
      setErrorMessage: setFormErrorMessage,
      setProcessMessage: setFormProcessingMessage,
      setResult: () => { setIsSignupComplete(true); },
    });
  };

  if (auth.isAuthenticated) {
    // redirect to index using react-router
    redirect("/");
    return null; // Prevent rendering the subscribe form
  }

  // 회원가입 완료 화면
  if (isSignupComplete) {
    return (
      <div className="auth-shell">
        <div className="auth-theme"><ThemeToggle /></div>
        <aside className="auth-aside">
          <div className="auth-brand"><span className="brand-dot" aria-hidden="true" />nprice</div>
          <p className="auth-lede">가격 비교 · 조정 · 작업이력</p>
        </aside>
        <div className="auth-panel">
          <CheckCircle2 className="signup-success-icon text-success mb-3" aria-hidden="true" />
          <h1 className="auth-title text-success">회원 가입 완료</h1>
          <p className="text-muted mb-4">
            회원가입이 완료되었습니다. 로그인 후 서비스를 이용할 수 있습니다.
          </p>
          <Link to="/login/" className="btn btn-primary">
            로그인하기
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-shell auth-shell-scroll">
      <div className="auth-theme"><ThemeToggle /></div>
      <aside className="auth-aside">
        <div className="auth-brand"><span className="brand-dot" aria-hidden="true" />nprice</div>
        <p className="auth-lede">가격 비교 · 조정 · 작업이력</p>
      </aside>
      <div className="auth-panel auth-card-wide">
          <h1 className="auth-title">회원가입</h1>
          {formErrorMessage && (
            <div className="alert alert-danger">{formErrorMessage}</div>
          )}
          {formProcessingMessage && (
            <div className="alert alert-info">{formProcessingMessage}</div>
          )}
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="mb-3">
              <label htmlFor="username" className="form-label">사용자명</label>
              <input
                type="text"
                className={`form-control ${errors.username ? "is-invalid" : ""}`}
                id="username"
                {...register("username", {
                  required: "사용자명을 입력하세요.",
                  minLength: {
                    value: 4,
                    message: "사용자명은 4자 이상이어야 합니다.",
                  },
                  maxLength: {
                    value: 16,
                    message: "사용자명은 16자 이하여야 합니다.",
                  },
                  pattern: {
                    value: USERNAME_PATTERN,
                    message: "사용자명은 영문, 숫자, 언더스코어(_)만 사용할 수 있습니다.",
                  },
                })}
              />
              <div className="form-text">영문, 숫자, 언더스코어(_)로 4~16자</div>
              {errors.username && (
                <div className="invalid-feedback">{errors.username.message}</div>
              )}
            </div>
            <div className="mb-3">
              <label htmlFor="password" className="form-label">비밀번호</label>
              <input
                type="password"
                className={`form-control ${errors.password ? "is-invalid" : ""}`}
                id="password"
                {...register("password", { required: "비밀번호를 입력하세요." })}
              />
              {errors.password && (
                <div className="invalid-feedback">{errors.password.message}</div>
              )}
            </div>
            <div className="mb-3">
              <label htmlFor="password_confirm" className="form-label">비밀번호 확인</label>
              <input
                type="password"
                className={`form-control ${errors.password_confirm ? "is-invalid" : ""}`}
                id="password_confirm"
                {...register("password_confirm", { required: "비밀번호 확인을 입력하세요." })}

              />
              {errors.password_confirm && (
                <div className="invalid-feedback">{errors.password_confirm.message}</div>
              )}
            </div>

            <div className="mb-3">
              <label htmlFor="bus_num" className="form-label">사업자등록번호</label>
              <input
                type="text"
                className={`form-control ${errors.bus_num ? "is-invalid" : ""}`}
                id="bus_num"
                placeholder="000-00-00000 (3자리-2자리-5자리)"
                maxLength={12}
                {...register("bus_num", {
                  required: "사업자등록번호를 입력하세요.",
                  validate: (value) => {
                    const cleaned = cleanBusinessNumber(value || '');
                    if (cleaned.length !== 10) {
                      return "사업자등록번호는 10자리여야 합니다.";
                    }
                    return true;
                  },
                  onChange: (e) => {
                    setIsBusinessValidated(false);
                    const formatted = formatBusinessNumber(e.target.value);
                    e.target.value = formatted;
                  }
                })}
              />
              {errors.bus_num && (
                <div className="invalid-feedback">{errors.bus_num.message}</div>
              )}
            </div>
            <div className="mb-3">
              <label htmlFor="rep_name" className="form-label">대표자명</label>
              <input
                type="text"
                className={`form-control ${errors.rep_name ? "is-invalid" : ""}`}
                id="rep_name"
                {...register("rep_name", {
                  required: "대표자명을 입력하세요.",
                  onChange: () => {
                    setIsBusinessValidated(false);
                  },
                })}
              />
              {errors.rep_name && (
                <div className="invalid-feedback">{errors.rep_name.message}</div>
              )}
            </div>
            <div className="mb-3">
              <label htmlFor="bus_started_at" className="form-label">개업연월일</label>
              <input
                type="text"
                className={`form-control ${errors.bus_started_at ? "is-invalid" : ""}`}
                id="bus_started_at"
                placeholder="YYYYMMDD 형식으로 입력"
                maxLength={8}
                {...register("bus_started_at", {
                  required: "개업연월일을 입력하세요.",
                  pattern: {
                    value: BUS_STARTED_AT_PATTERN,
                    message: "개업연월일은 숫자 8자리(YYYYMMDD)로 입력하세요.",
                  },
                  onChange: (event) => {
                    setIsBusinessValidated(false);
                    event.target.value = (event.target.value || '').replace(/[^\d]/g, '').slice(0, 8);
                  },
                })}
              />
              {errors.bus_started_at && (
                <div className="invalid-feedback">{errors.bus_started_at.message}</div>
              )}
            </div>
            <div className="mb-3">
              <button
                type="button"
                className="btn btn-outline-primary w-100"
                onClick={handleBusinessValidation}
                disabled={!busNum || cleanBusinessNumber(busNum).length !== 10 || !repName || !busStartedAt}
              >
                사업자 정보 검증
              </button>
              {businessValidationMessage && (
                <div className={`mt-2 alert ${isBusinessValidated ? 'alert-success' : 'alert-warning'}`}>
                  {businessValidationMessage}
                </div>
              )}
            </div>


            <button type="submit" className="btn btn-primary w-100">회원가입</button>
          </form>
          <hr />
          <Link to="/login/" className="btn btn-outline-dark w-100">취소</Link>
      </div>
    </div>
  );
}
export default SignUpPage;
